import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useImpersonationCtx } from '../../context/ImpersonationContext';
import { useUserCtx } from '../../context/UserContext';
import { getUserName } from '../../utils/user.util';

function ImpersonationSelector() {
  const { canImpersonate, isImpersonating, impersonatingUser, startImpersonating, stopImpersonating } =
    useImpersonationCtx();
  const { users } = useUserCtx();
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Don't render if user can't impersonate, but also handle case where currUser might not be loaded yet
  // Show button if impersonating (even if canImpersonate check fails temporarily) to allow stopping
  if (!canImpersonate && !isImpersonating) {
    return null;
  }

  const handleOpen = () => {
    setOpen(true);
    setSelectedUser(null);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
  };

  const handleStartImpersonating = () => {
    if (selectedUser) {
      startImpersonating(selectedUser);
      handleClose();
    }
  };

  const handleStopImpersonating = () => {
    stopImpersonating();
  };

  return (
    <>
      {isImpersonating ? (
        <Button
          variant="outlined"
          color="warning"
          startIcon={<PersonIcon />}
          onClick={handleStopImpersonating}
          sx={{ mr: 1 }}
        >
          Stop Impersonating {getUserName(impersonatingUser)}
        </Button>
      ) : (
        <Button variant="outlined" startIcon={<PersonIcon />} onClick={handleOpen} sx={{ mr: 1 }}>
          Impersonate User
        </Button>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Select User to Impersonate</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              You will see the application as the selected user sees it. All actions will be recorded under your
              account.
            </Alert>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => `${getUserName(option)} (${option.email})`}
              value={selectedUser}
              onChange={(event, newValue) => setSelectedUser(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select User" placeholder="Search by name or email" />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{getUserName(option)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.email} • {option.role}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleStartImpersonating} variant="contained" disabled={!selectedUser}>
            Start Impersonating
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ImpersonationSelector;
